/**
 * Authenticator-Apps und Passwortmanager, die die Einrichtung erklaert.
 *
 * **Warum es diese Liste gibt:** die erste echte Einrichtung eines Bestandskunden
 * (2026-10-08) bestand aus Rueckfragen — welche App, wo tippe ich, wie scanne ich einen
 * QR-Code auf demselben Geraet. Jede Antwort, die hier steht, ist eine, die der Betreiber
 * nicht am Telefon geben muss.
 *
 * Nur Bezeichnungen der Apps selbst, keine Logos: fremde Marken als Bild gehoeren nicht in
 * eine Anmeldeseite, und der Name ist das, was man im App-Store sucht.
 *
 * Die Schritte sind bewusst knapp und nennen nur, was in allen aktuellen Fassungen gleich
 * heisst. Wer einen Schritt aendert, prueft ihn an der echten App.
 */

export interface AuthenticatorApp {
  id: string;
  name: string;
  /** Wo es laeuft und was es kostet — die zweite Frage nach „welche App". */
  hinweis: string;
  schritte: [string, string, string];
}

export const AUTHENTICATOR_APPS: AuthenticatorApp[] = [
  {
    id: "google",
    name: "Google Authenticator",
    hinweis: "iPhone und Android, kostenlos",
    schritte: [
      "App öffnen und unten rechts auf ＋ tippen.",
      "„QR-Code scannen“ wählen — oder „Einrichtungsschlüssel eingeben“, wenn du auf diesem Gerät bist.",
      "Unter „Janeway“ erscheint ein sechsstelliger Code. Den gibst du unten ein.",
    ],
  },
  {
    id: "microsoft",
    name: "Microsoft Authenticator",
    hinweis: "iPhone und Android, kostenlos",
    schritte: [
      "Oben rechts auf ＋ tippen und „Anderes Konto (Google, Facebook usw.)“ wählen.",
      "QR-Code scannen — oder „Code manuell eingeben“ und den Schlüssel einfügen.",
      "Den sechsstelligen Code unter „Janeway“ unten eingeben.",
    ],
  },
  {
    id: "apple",
    name: "Apple Passwörter",
    hinweis: "iPhone, iPad und Mac, ab iOS 18 / macOS 15 eingebaut",
    schritte: [
      "App „Passwörter“ öffnen und „Codes“ wählen.",
      "Auf ＋ tippen, dann QR-Code scannen oder Einrichtungsschlüssel eingeben. Am iPhone geht es auch direkt über „In Authenticator-App öffnen“.",
      "Den Code unter „Janeway“ unten eingeben.",
    ],
  },
  {
    id: "bitwarden",
    name: "Bitwarden",
    hinweis: "Passwortmanager (Premium) oder die kostenlose App Bitwarden Authenticator",
    schritte: [
      "Im Passwortmanager deinen Janeway-Eintrag bearbeiten — oder in Bitwarden Authenticator auf ＋ tippen.",
      "Beim Feld „Authentifizierungsschlüssel (TOTP)“ den QR-Code scannen oder den Schlüssel einfügen und speichern.",
      "Bitwarden zeigt den sechsstelligen Code im Eintrag. Den gibst du unten ein.",
    ],
  },
  {
    id: "1password",
    name: "1Password",
    hinweis: "Passwortmanager, auf allen Geräten",
    schritte: [
      "Deinen Janeway-Eintrag öffnen und „Bearbeiten“ wählen.",
      "Ein Feld „Einmalpasswort“ hinzufügen und den QR-Code scannen oder den Schlüssel einfügen. Am Computer liest 1Password den QR-Code direkt vom Bildschirm.",
      "Speichern und den angezeigten Code unten eingeben.",
    ],
  },
  {
    id: "aegis",
    name: "Aegis",
    hinweis: "Android, kostenlos und quelloffen",
    schritte: [
      "Unten rechts auf ＋ tippen.",
      "„QR-Code scannen“ oder „Manuell eingeben“ wählen.",
      "Den Code unter „Janeway“ unten eingeben.",
    ],
  },
  {
    id: "2fas",
    name: "2FAS",
    hinweis: "iPhone und Android, kostenlos",
    schritte: [
      "Auf ＋ tippen.",
      "QR-Code scannen — oder „Manuell eingeben“ und den Schlüssel einfügen.",
      "Den Code unter „Janeway“ unten eingeben.",
    ],
  },
];

/**
 * Der Schluessel zum Abtippen, in Vierergruppen.
 *
 * GoTrue liefert 32 Zeichen am Stueck. Vom Bildschirm in ein Telefon abgeschrieben ist das
 * die Stelle, an der man verrutscht; Leerzeichen akzeptieren alle hier genannten Apps.
 */
export function gruppiereSchluessel(geheimnis: string): string {
  return (geheimnis.replace(/\s+/g, "").match(/.{1,4}/g) ?? []).join(" ");
}

/**
 * GoTrue-Fehler auf Deutsch, mit dem, was zu tun ist.
 *
 * Bis 0.573.x stand die englische Meldung roh im Feld („Invalid TOTP code entered") —
 * genau der Moment, in dem jemand ohnehin unsicher ist, ob er alles richtig gemacht hat.
 */
export function zweitfaktorFehlerText(meldung: string | undefined): string {
  const m = (meldung ?? "").toLowerCase();
  if (/invalid totp|invalid code|code.*invalid/.test(m)) {
    return "Der Code stimmt nicht. Nimm den Code, der gerade in der App steht — er wechselt alle 30 Sekunden. " +
      "Klappt es weiter nicht, prüfe, ob die Uhrzeit am Handy automatisch gestellt ist.";
  }
  if (/expired|challenge/.test(m)) {
    return "Der Code ist abgelaufen. Bitte den aktuell angezeigten Code eingeben.";
  }
  if (/rate|too many/.test(m)) {
    return "Zu viele Versuche. Bitte einen Moment warten und dann erneut versuchen.";
  }
  return meldung || "Das hat nicht geklappt. Bitte erneut versuchen.";
}
